/**
 * Safe API utilities for the widget.
 * Provides timeout-aware fetch, retry logic, and safe JSON parsing
 * to prevent "failed to execute JSON on response" errors.
 */

/** Result type for safe JSON parsing */
export type SafeJsonResult<T = unknown> =
  | { ok: true; data: T }
  | { ok: false; error: string };

/**
 * Safely parse a Response as JSON without throwing.
 * Reads the body as text first, then parses — catches both
 * empty bodies and non-JSON responses (HTML error pages, etc).
 */
export async function safeJsonParse<T = unknown>(
  response: Response
): Promise<SafeJsonResult<T>> {
  try {
    const text = await response.text();
    if (!text.trim()) {
      return { ok: false, error: `Empty response body (HTTP ${response.status})` };
    }
    const data = JSON.parse(text) as T;
    return { ok: true, data };
  } catch {
    return {
      ok: false,
      error: `Invalid JSON response (HTTP ${response.status}: ${response.statusText})`,
    };
  }
}

/**
 * Fetch with a timeout using AbortController.
 * Throws if the request exceeds `timeoutMs`.
 */
export async function fetchWithTimeout(
  url: string,
  options: RequestInit = {},
  timeoutMs: number = 30000
): Promise<Response> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);

  try {
    const response = await fetch(url, {
      ...options,
      signal: controller.signal,
    });
    return response;
  } catch (err) {
    if (err instanceof DOMException && err.name === 'AbortError') {
      throw new Error(`Request timed out after ${Math.round(timeoutMs / 1000)}s`);
    }
    throw err;
  } finally {
    clearTimeout(timer);
  }
}

/**
 * Fetch with automatic retries and exponential backoff.
 * Only retries on network errors / timeouts, NOT on HTTP error codes.
 */
export async function fetchWithRetry(
  url: string,
  options: RequestInit = {},
  config: {
    maxRetries?: number;
    timeoutMs?: number;
    backoffMs?: number;
  } = {}
): Promise<Response> {
  const { maxRetries = 3, timeoutMs = 30000, backoffMs = 1000 } = config;

  let lastError: Error | null = null;

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      return await fetchWithTimeout(url, options, timeoutMs);
    } catch (err) {
      lastError = err instanceof Error ? err : new Error(String(err));

      // Don't retry if this was the last attempt
      if (attempt < maxRetries) {
        const delay = backoffMs * Math.pow(3, attempt); // 1s, 3s, 9s
        await new Promise((resolve) => setTimeout(resolve, delay));
      }
    }
  }

  throw lastError || new Error('Request failed after retries');
}
